// /pricing - exactly the composition of the mockup «Pricing» (ADR-0031), pre-rendered at build (ADR-0036): every
// heading, the plan, the list and the answers are plain HTML. The client refines only the plan button by session.
import { Title } from "./Hl";
import { useState } from "react";
import { Link } from "react-router";
import { t, type MessageKey } from "../i18n";
import type { BillingPeriod } from "../api";
import { PRICING_FAQ, PRICING_FAQ_CLIENTS_Q } from "../landing";
import { CLIENTS_NEW, CLIENTS_WORD } from "../lib";
import { useSession } from "../session";
import { CaretDownIcon, CheckIcon } from "./icons";
import { SUBSCRIBED, keepsTrialDays, useBillingPlan } from "./SubscribePage";

const INCLUDED = ["sources", "assistants", "accounts", "clients", "privacy"] as const;

const NOT_HERE = [1, 2, 3, 4, 5] as const;
const DAYS = [1, 21, 29, 31] as const;

export const SIGN_IN_TO_SUBSCRIBE = `/signin?return_to=${encodeURIComponent("/subscribe")}`;

/**
 * The plan button by session: signed out (and the pre-render) - «Start 30 free days» through sign-in back to
 * /subscribe; signed in - /subscribe; subscribed - Account; complimentary - a link to Account, no Subscribe.
 */
function PlanAction() {
  const { me } = useSession();
  const plan = useBillingPlan(Boolean(me));
  let to = SIGN_IN_TO_SUBSCRIBE;
  let label = t("pricing.start");
  let hint = t("pricing.startHint");
  let primary = true;
  if (me && plan?.state === "off") {
    return <p className="pricing-hint pricing-soon">{t("pricing.openSoon")}</p>;
  }
  if (me) {
    to = "/subscribe";
    if (plan && SUBSCRIBED.includes(plan.state)) {
      to = "/account";
      label = t("pricing.manage");
      hint = t("pricing.subscribedHint");
    } else if (plan?.state === "comp") {
      to = "/account";
      label = t("pricing.compLink");
      hint = t("pricing.compHint");
      primary = false;
    } else if (plan?.state === "gift") {
      label = t("pricing.subscribe");
      hint = t("pricing.giftHint");
    } else if (plan && keepsTrialDays(plan)) {
      label = t("pricing.subscribe");
      hint = t("pricing.trialHint", { days: plan.days_left ?? 0 });
    } else if (plan?.state === "expired" || plan?.state === "trial" || plan?.state === "trial-ending") {
      label = t("pricing.subscribe");
      hint = plan.state === "expired" ? t("pricing.expiredHint") : t("pricing.startHint");
    }
  }
  return (
    <div className="pricing-action">
      <Link className={`btn ${primary ? "btn-primary" : "btn-secondary"} btn-block pricing-cta`} to={to} data-testid="pricing-cta">
        {label}
      </Link>
      <span className="pricing-hint">{hint}</span>
    </div>
  );
}

/** More than three clients: the request form behind sign-in, Support for a guest (and in the pre-render). */
function MoreClientsLink() {
  const { me } = useSession();
  return (
    <Link className="text-link" to="/clients/more" data-testid="pricing-more-clients">
      {t("pricing.faqClients.more")}&nbsp;→
    </Link>
  );
}

export function PricingPage() {
  const [period, setPeriod] = useState<BillingPeriod>("monthly");
  const yearly = period === "yearly";
  return (
    <article className="pricing">
      <header className="pricing-hero">
        <div className="pricing-copy">
          <h6 className="kicker">{t("pricing.kicker")}</h6>
          <h1><Title text={t("pricing.h1")} dot="sky" /></h1>
          <p className="hero-lead">{t("pricing.lead")}</p>
          <ul className="pricing-incl">
            {INCLUDED.map((k) => (
              <li key={k}>
                <CheckIcon size={16} />
                <span>
                  {t(`pricing.incl.${k}`, { count: CLIENTS_WORD })}
                  {k === "clients" && (
                    <>
                      {" · "}
                      <Link className="text-link" to="/clients/more" data-testid="pricing-incl-more">
                        {t("pricing.incl.clientsMore")}
                      </Link>
                    </>
                  )}
                  {k === "clients" && CLIENTS_NEW && <span className="pricing-new">{t("pricing.new")}</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <aside aria-label={t("pricing.plan")} className="card elev-md pricing-plan">
          <div className="pricing-plan-head">
            <span className="pricing-label">{t("pricing.onlyPlan")}</span>
            <div role="radiogroup" aria-label={t("pricing.period")} className="pill-toggle">
              {(["monthly", "yearly"] as const).map((p) => (
                <button key={p} type="button" role="radio" aria-checked={period === p} data-testid={`pricing-${p}`} onClick={() => setPeriod(p)}>
                  {t(`pricing.${p}`)}
                </button>
              ))}
            </div>
          </div>
          <div className="pricing-price">
            <div>
              <span className="pricing-amount" data-testid="pricing-amount">{t(yearly ? "pricing.priceYearly" : "pricing.priceMonthly")}</span>
              <span className="pricing-unit">{t(yearly ? "pricing.perYear" : "pricing.perMonth")}</span>
            </div>
            <span className="pricing-note">{t(yearly ? "pricing.noteYearly" : "pricing.noteMonthly")}</span>
          </div>
          <PlanAction />
          <div className="pricing-rule" />
          <dl className="pricing-terms">
            <dt>{t("pricing.vat")}</dt>
            <dd>{t("pricing.vatText")}</dd>
            <dt>{t("pricing.cancel")}</dt>
            <dd>{t("pricing.cancelText")}</dd>
            <dt>{t("pricing.refund")}</dt>
            <dd>
              {t("pricing.refundText")} <a href="/refunds.html">{t("pricing.refundLink")}</a>
            </dd>
          </dl>
        </aside>
      </header>

      <section aria-labelledby="not-here" className="pricing-section">
        <div className="pricing-section-head">
          <h2 id="not-here">{t("pricing.notHere")}</h2>
          <p>{t("pricing.notHereLead")}</p>
        </div>
        <div className="pricing-nothere">
          {NOT_HERE.map((n) => (
            <div key={n} className="card elev-sm">
              <s>{t(`pricing.nh${n}.title` as MessageKey)}</s>
              <span>{t(`pricing.nh${n}.body` as MessageKey)}</span>
            </div>
          ))}
        </div>
      </section>

      <section aria-labelledby="how-billing" className="pricing-how">
        <div className="pricing-section-head">
          <h2 id="how-billing">{t("pricing.how")}</h2>
          <p>{t("pricing.howLead")}</p>
        </div>
        <ol className="pricing-days">
          {DAYS.map((d) => (
            <li key={d}>
              <span className="pricing-day">{t(`pricing.day${d}` as MessageKey)}</span>
              <span>{t(`pricing.day${d}Text` as MessageKey)}</span>
            </li>
          ))}
        </ol>
      </section>

      <section aria-labelledby="faq" className="pricing-section pricing-faq">
        <h2 id="faq">{t("pricing.faq")}</h2>
        <div>
          {PRICING_FAQ.map((f, i) => (
            <details key={f.q} open={i === 0}>
              <summary>
                {f.q}
                <CaretDownIcon size={14} />
              </summary>
              <p>
                {f.a}
                {f.q === PRICING_FAQ_CLIENTS_Q && (
                  <>
                    {" "}
                    <MoreClientsLink />
                  </>
                )}
              </p>
            </details>
          ))}
        </div>
      </section>
    </article>
  );
}
